import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChargerGoogleMap } from '../../components/charger_planning/ChargerGoogleMap';
import { LandParcelSuggestionPanel } from '../../components/charger_planning/LandParcelSuggestionPanel';
import LoadingIndicator from '../../components/general/LoadingIndicator';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/chargerApiConfig';
import { logger } from '../../utils/logger';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { useParcels } from '../../contexts/ParcelContext.jsx';
import { initializeGeocoder, geocodeAddress } from '../../utils/geocodingUtil';
import {
    FiZap,
    FiPackage,
    FiShare2,
    FiTool,
    FiMapPin,
    FiTrendingUp,
    FiGrid,
    FiFlag,
    FiAlertTriangle
} from 'react-icons/fi';
import { PiTrafficSignal } from 'react-icons/pi';

const TOP_PARCEL_LABEL_LIMIT = 100;
const CHARGER_MAP_LAYER_PREFS_KEY = 'charger_map_layer_prefs';
const DEFAULT_CHARGER_MAP_LAYER_PREFS = {
    landParcelCheckbox: true,
    SCAQMDCheckbox: false,
    sraCheckbox: false,
    mhdInfrastructureCheckbox: false,
    warehouseCheckbox: false,
    truckStopCheckbox: false,
    intermodalTerminalCheckbox: false,
    serviceCenterCheckbox: false,
    truckParkingLocationCheckbox: false,
    pemsTrafficCheckbox: false,
    trafficMainlineCheckbox: true,
    trafficOnrampCheckbox: true,
    trafficOfframpCheckbox: true,
    disadvantagedCommunitiesCheckbox: false,
    calEnviroScreenCheckbox: false,
    calEnviroScreenMinPercentile: 75,
    circuitCapacityCheckbox: false,
    utilitiesCheckbox: false,
};

const clampCalEnviroPercentile = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return DEFAULT_CHARGER_MAP_LAYER_PREFS.calEnviroScreenMinPercentile;
    return Math.max(0, Math.min(100, n));
};

const normalizeChargerMapLayerPrefs = (parsed) => {
    if (!parsed || typeof parsed !== 'object') return DEFAULT_CHARGER_MAP_LAYER_PREFS;
    return {
        landParcelCheckbox: Boolean(parsed.landParcelCheckbox),
        SCAQMDCheckbox: Boolean(parsed.SCAQMDCheckbox),
        sraCheckbox: Boolean(parsed.sraCheckbox),
        mhdInfrastructureCheckbox: Boolean(parsed.mhdInfrastructureCheckbox),
        warehouseCheckbox: Boolean(parsed.warehouseCheckbox),
        truckStopCheckbox: Boolean(parsed.truckStopCheckbox),
        intermodalTerminalCheckbox: Boolean(parsed.intermodalTerminalCheckbox),
        serviceCenterCheckbox: Boolean(parsed.serviceCenterCheckbox),
        truckParkingLocationCheckbox: Boolean(parsed.truckParkingLocationCheckbox),
        pemsTrafficCheckbox: Boolean(parsed.pemsTrafficCheckbox),
        trafficMainlineCheckbox: Boolean(parsed.trafficMainlineCheckbox),
        trafficOnrampCheckbox: Boolean(parsed.trafficOnrampCheckbox),
        trafficOfframpCheckbox: Boolean(parsed.trafficOfframpCheckbox),
        disadvantagedCommunitiesCheckbox: Boolean(parsed.disadvantagedCommunitiesCheckbox),
        calEnviroScreenCheckbox: Boolean(parsed.calEnviroScreenCheckbox),
        calEnviroScreenMinPercentile: clampCalEnviroPercentile(parsed.calEnviroScreenMinPercentile),
        circuitCapacityCheckbox: Boolean(parsed.circuitCapacityCheckbox),
        utilitiesCheckbox: Boolean(parsed.utilitiesCheckbox),
    };
};

const readChargerMapLayerPrefs = () => {
    if (typeof window === 'undefined') return DEFAULT_CHARGER_MAP_LAYER_PREFS;
    try {
        const stored = localStorage.getItem(CHARGER_MAP_LAYER_PREFS_KEY);
        if (!stored) return DEFAULT_CHARGER_MAP_LAYER_PREFS;
        const parsed = JSON.parse(stored);
        return normalizeChargerMapLayerPrefs(parsed);
    } catch (_) {
        return DEFAULT_CHARGER_MAP_LAYER_PREFS;
    }
};

const PARCEL_ID_KEYS = [
    'apn',
    'APN',
    'parcel_apn',
    'ParcelAPN',
    'parcelId',
    'parcel_id',
    'ParcelID',
    'Parcel_Id',
    'id',
    'ID'
];

const LAND_VALUE_FIELDS = [
    'land_value',
    'Land_Value',
    'landValue',
    'LandValue',
    'avg_land_value',
    'Average_Land_Value',
    'average_land_value',
    'cluster_land_value',
    'Cluster_Land_Value'
];

const extractParcelIdentifier = (parcel) => {
    if (!parcel || typeof parcel !== 'object') return null;

    for (const key of PARCEL_ID_KEYS) {
        const value = parcel?.[key];
        if (value !== undefined && value !== null && value !== '') {
            return String(value).toLowerCase();
        }
    }

    return null;
};

const normalizeParcelIdentifier = (parcel) => {
    return (
        extractParcelIdentifier(parcel) ||
        extractParcelIdentifier(parcel?.land_parcel_data) ||
        null
    );
};

const parseLandValueFromParcel = (parcel) => {
    if (!parcel || typeof parcel !== 'object') return null;

    for (const key of LAND_VALUE_FIELDS) {
        const rawValue = parcel?.[key];
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;

        const normalized = String(rawValue).trim().replace(/[^0-9.-]/g, '');
        if (!normalized) continue;

        const numericValue = Number(normalized);
        if (!Number.isNaN(numericValue)) {
            return numericValue;
        }
    }

    if (parcel?.land_parcel_data) {
        return parseLandValueFromParcel(parcel.land_parcel_data);
    }

    return null;
};

function ChargerMapView() {
    const initialLayerPrefsRef = useRef(null);
    if (!initialLayerPrefsRef.current) {
        initialLayerPrefsRef.current = readChargerMapLayerPrefs();
    }
    const initialLayerPrefs = initialLayerPrefsRef.current;

    // Configuration for all loading indicators
    const getLoadingIndicators = () => [
        { 
            isVisible: parcelLoading, 
            text: 'Loading Land Parcel Data...', 
            color: 'gray' 
        },
        { 
            isVisible: isLoadingMore, 
            text: 'Loading more parcels...', 
            color: 'blue' 
        },
        { 
            isVisible: scaqmdJurisdictionLoading, 
            text: 'Loading SCAQMD Jurisdiction Data...', 
            color: 'gray' 
        },
        {
            isVisible: sraDataLoading,
            text: 'Loading SCAQMD Source Receptor Areas Data...',
            color: 'gray'
        },
        { 
            isVisible: mhdInfrastructureLoading, 
            text: 'Loading MHD Infrastructure Data...', 
            color: 'gray' 
        },
        { 
            isVisible: warehousesLoading, 
            text: 'Loading Warehouse Data...', 
            color: 'gray' 
        },
        { 
            isVisible: heatmapDataLoading, 
            text: 'Loading Heatmap Data...', 
            color: 'gray' 
        },
        { 
            isVisible: disadvantagedCommunitiesLoading, 
            text: 'Loading Disadvantaged Communities Data...', 
            color: 'gray' 
        },
        {
            isVisible: calEnviroScreenLoading,
            text: 'Loading CalEnviroScreen 4.0 Data...',
            color: 'gray'
        },
        { 
            isVisible: truckStopsLoading, 
            text: 'Loading Truck Stops Data...', 
            color: 'gray' 
        },
        { 
            isVisible: circuitDataLoading, 
            text: 'Loading Circuit Data...', 
            color: 'gray' 
        },
        { 
            isVisible: trafficDataLoading, 
            text: 'Loading Traffic Data...', 
            color: 'gray' 
        },
        {
            isVisible: utilitiesDataLoading, 
            text: 'Loading Utilities Data...', 
            color: 'gray' 
        }
    ];

    // Data layers
    const [scaqmdJurisdiction, setScaqmdJurisdiction] = useState([]);
    const [scaqmdJurisdictionLoading, setScaqmdJurisdictionLoading] = useState(false);
    const [sraData, setSraData] = useState([]);
    const [sraDataLoading, setSraDataLoading] = useState(false);
    const [mhdInfrastructure, setMhdInfrastructure] = useState([]);
    const [mhdInfrastructureLoading, setMhdInfrastructureLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [warehousesLoading, setWarehousesLoading] = useState(false);
    const [heatmapData] = useState([]);
    const [heatmapDataLoading] = useState(false);
    const [disadvantagedCommunities, setDisadvantagedCommunities] = useState([]);
    const [disadvantagedCommunitiesLoading, setDisadvantagedCommunitiesLoading] = useState(false);
    const [calEnviroScreen, setCalEnviroScreen] = useState([]);
    const [calEnviroScreenLoading, setCalEnviroScreenLoading] = useState(false);
    const [truckStops, setTruckStops] = useState([]);
    const [truckStopsLoading, setTruckStopsLoading] = useState(false);
    const [circuitData, setCircuitData] = useState([]);
    const [circuitDataLoading, setCircuitDataLoading] = useState(false);
    const [circuitContours, setCircuitContours] = useState([]);
    const [currentZoom, setCurrentZoom] = useState(10);
    const [trafficData, setTrafficData] = useState([]);
    const [trafficDataLoading, setTrafficDataLoading] = useState(false);
    const [utilitiesData, setUtilitiesData] = useState([]);
    const [utilitiesDataLoading, setUtilitiesDataLoading] = useState(false);
    const [searchId, setSearchId] = useState(null);
    const [parcelPointTileUrl, setParcelPointTileUrl] = useState(API_ENDPOINTS.vectorTiles.parcelPoints);
    const [parcelPolygonTileUrl, setParcelPolygonTileUrl] = useState(API_ENDPOINTS.vectorTiles.parcels);
    const [nextCursor, setNextCursor] = useState(null);

    // Data layer visibility state
    const [landParcelCheckbox] = useState(initialLayerPrefs.landParcelCheckbox);
    const [SCAQMDCheckbox, setSCAQMDCheckbox] = useState(initialLayerPrefs.SCAQMDCheckbox);
    const [sraCheckbox, setSraCheckbox] = useState(initialLayerPrefs.sraCheckbox);
    const [mhdInfrastructureCheckbox, setMhdInfrastructureCheckbox] = useState(initialLayerPrefs.mhdInfrastructureCheckbox);
    const [warehouseCheckbox, setWarehouseCheckbox] = useState(initialLayerPrefs.warehouseCheckbox);
    const [truckStopCheckbox] = useState(initialLayerPrefs.truckStopCheckbox);
    const [intermodalTerminalCheckbox, setIntermodalTerminalCheckbox] = useState(initialLayerPrefs.intermodalTerminalCheckbox);
    const [serviceCenterCheckbox, setServiceCenterCheckbox] = useState(initialLayerPrefs.serviceCenterCheckbox);
    const [truckParkingLocationCheckbox, setTruckParkingLocationCheckbox] = useState(initialLayerPrefs.truckParkingLocationCheckbox);
    const [pemsTrafficCheckbox, setPemsTrafficCheckbox] = useState(initialLayerPrefs.pemsTrafficCheckbox);
    const [trafficMainlineCheckbox] = useState(initialLayerPrefs.trafficMainlineCheckbox);
    const [trafficOnrampCheckbox] = useState(initialLayerPrefs.trafficOnrampCheckbox);
    const [trafficOfframpCheckbox] = useState(initialLayerPrefs.trafficOfframpCheckbox);
    const [disadvantagedCommunitiesCheckbox, setDisadvantagedCommunitiesCheckbox] = useState(initialLayerPrefs.disadvantagedCommunitiesCheckbox);
    const [calEnviroScreenCheckbox, setCalEnviroScreenCheckbox] = useState(initialLayerPrefs.calEnviroScreenCheckbox);
    const [calEnviroScreenMinPercentile, setCalEnviroScreenMinPercentile] = useState(initialLayerPrefs.calEnviroScreenMinPercentile);
    const [circuitCapacityCheckbox, setCircuitCapacityCheckbox] = useState(initialLayerPrefs.circuitCapacityCheckbox);
    const [utilitiesCheckbox, setUtilitiesCheckbox] = useState(initialLayerPrefs.utilitiesCheckbox);

    const { isLoaded, loadError } = useGoogleMaps();
    const {
        setAllParcels,
        // Persistent map view state
        mapViewState,
        updateMapViewState,
        updateMapPosition,
        updateParcelDisplay,
        updateSearchState,
        updateHighlightedParcel,
    } = useParcels();

    // Use persistent state from context
    const {
        parcelData,
        mapParcelData,
        searchFilters,
        currentPage,
        hasNextPage,
        totalFilteredCount,
        parcelLoading,
        isLoadingMore,
        isFiltering,
        mapCenter,
        mapZoom,
        mapBounds,
        highlightedParcelId
    } = mapViewState;
    const parcelSelection = mapParcelData.length > 0 ? mapParcelData : parcelData;

    const extractLatLon = (parcel) => {
        const latFields = ['Center_Lat', 'center_lat', 'lat', 'latitude', 'Lat'];
        const lonFields = ['Center_Lon', 'center_lon', 'lon', 'longitude', 'Lon', 'lng'];
        let lat = null;
        let lon = null;

        for (const key of latFields) {
            if (parcel?.[key] !== undefined) {
                lat = parseFloat(parcel[key]);
                if (!Number.isNaN(lat)) break;
            }
        }
        for (const key of lonFields) {
            if (parcel?.[key] !== undefined) {
                lon = parseFloat(parcel[key]);
                if (!Number.isNaN(lon)) break;
            }
        }

        return { lat, lon };
    };

    const topParcelPoints = useMemo(() => {
        if (!Array.isArray(parcelSelection)) return [];

        return parcelSelection
            .slice(0, TOP_PARCEL_LABEL_LIMIT)
            .map(parcel => {
                const identifier = normalizeParcelIdentifier(parcel);
                if (!identifier) return null;

                const { lat, lon } = extractLatLon(parcel);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

                const landValue = parseLandValueFromParcel(parcel);

                return {
                    id: identifier,
                    lat,
                    lon,
                    landValue,
                    parcel
                };
            })
            .filter(Boolean);
    }, [parcelSelection]);

    useEffect(() => {
        logger.log('Top parcel points snapshot:', {
            count: Array.isArray(topParcelPoints) ? topParcelPoints.length : 0,
            sample: Array.isArray(topParcelPoints) ? topParcelPoints.slice(0, 3) : []
        });
    }, [topParcelPoints]);

    const PAGE_SIZE = 50;
    const PANEL_PAGE_SIZE = 20;

    const deriveParcelScore = useCallback((parcel) => {
        if (!parcel || typeof parcel !== 'object') return null;
        const scoreCandidate =
            parcel.recommendation_score ??
            parcel.weighted_score ??
            parcel.score ??
            parcel?.land_parcel_data?.recommendation_score ??
            parcel?.land_parcel_data?.score ??
            null;

        if (scoreCandidate === null || scoreCandidate === undefined || scoreCandidate === '') {
            return null;
        }

        const numericScore = Number(scoreCandidate);
        if (!Number.isNaN(numericScore)) {
            return numericScore;
        }

        return typeof scoreCandidate === 'string' ? scoreCandidate : null;
    }, []);

    const normalizeParcelForDisplay = useCallback((parcel) => {
        if (!parcel || typeof parcel !== 'object') return parcel;

        const flattened = parcel.land_parcel_data && typeof parcel.land_parcel_data === 'object'
            ? { ...parcel.land_parcel_data, ...parcel }
            : { ...parcel };

        // Map backend snake_case to the camel/cased keys the UI expects
        const aliases = {
            Address: flattened.Address ?? flattened.address ?? flattened.site_address ?? null,
            APN: flattened.APN ?? flattened.apn ?? flattened.parcel_apn ?? null,
            Land_Value: flattened.Land_Value ?? flattened.land_value ?? flattened.landValue ?? null,
            Area: flattened.Area ?? flattened.area ?? flattened.area_sqft ?? flattened.parcel_area_sqft ?? null,
            Zip5: flattened.Zip5 ?? flattened.zip5 ?? flattened.zip ?? flattened.zip_code ?? null,
            County_par: flattened.County_par ?? flattened.county ?? flattened.county_name ?? null,
            zone_cla_1: flattened.zone_cla_1 ?? null,
            utility_region_acronym: flattened.utility_region_acronym ?? null,
            utility_region_name: flattened.utility_region_name ?? null,
            Center_Lat: flattened.Center_Lat ?? flattened.center_lat ?? flattened.lat ?? null,
            Center_Lon: flattened.Center_Lon ?? flattened.center_lon ?? flattened.lon ?? flattened.lng ?? flattened.longitude ?? null,
            id: flattened.id ?? flattened.parcel_id ?? flattened.parcelId ?? flattened.apn ?? flattened.APN ?? null,
        };

        return {
            ...flattened,
            ...aliases,
            recommendation_score: deriveParcelScore(flattened)
        };
    }, [deriveParcelScore]);

    const normalizeParcelList = useCallback((list = []) => {
        if (!Array.isArray(list)) return [];
        return list.map(normalizeParcelForDisplay);
    }, [normalizeParcelForDisplay]);

    const mapRef = useRef(null);
    const lastBoundsRef = useRef(null);
    const loadingTimeoutRef = useRef(null);
    // const abortControllerRef = useRef(null); // Add abort controller for cleanup
    const heatmapTimeoutRef = useRef(null); // Debounce timer for heatmap auto-fetch
    const hasRestoredInitialViewRef = useRef(false);

    // Map state for viewport-based loading  
    const [prevMapBounds, setPrevMapBounds] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(mapZoom || 10);

    const parcelTileFilters = useMemo(() => {
        const mapKeys = {
            minLandValue: 'value_min',
            maxLandValue: 'value_max',
            minArea: 'area_min',
            maxArea: 'area_max',
            zipCode: 'zip',
            city: 'city',
            utility: 'utility_region_acronym',
            inScaqmdJurisdiction: 'near_scaqmd',
            inDisadvantagedCommunity: 'near_dc',
            min_score: 'min_score'
        };

        const params = {};
        Object.entries(mapKeys).forEach(([localKey, apiKey]) => {
            const value = searchFilters?.[localKey];
            if (value !== undefined && value !== null && value !== '') {
                params[apiKey] = value;
            }
        });

        params.top = Math.min(TOP_PARCEL_LABEL_LIMIT, 200);

        return params;
    }, [searchFilters]);

    const trafficStationFilters = useMemo(() => ({
        mainline: trafficMainlineCheckbox,
        onramp: trafficOnrampCheckbox,
        offramp: trafficOfframpCheckbox
    }), [trafficMainlineCheckbox, trafficOnrampCheckbox, trafficOfframpCheckbox]);

    const filteredTrafficData = useMemo(() => {
        if (!Array.isArray(trafficData) || trafficData.length === 0) {
            return [];
        }

        const activeTypes = new Set();
        if (trafficMainlineCheckbox) activeTypes.add('ML');
        if (trafficOnrampCheckbox) activeTypes.add('OR');
        if (trafficOfframpCheckbox) activeTypes.add('FR');
        if (activeTypes.size === 0) {
            return [];
        }

        return trafficData.filter(point => {
            const normalizedType = (point.station_type || 'ML').toString().trim().toUpperCase();
            return activeTypes.has(normalizedType);
        });
    }, [trafficData, trafficMainlineCheckbox, trafficOnrampCheckbox, trafficOfframpCheckbox]);

    useEffect(() => {
        try {
            localStorage.setItem(
                CHARGER_MAP_LAYER_PREFS_KEY,
                JSON.stringify({
                    landParcelCheckbox,
                    SCAQMDCheckbox,
                    sraCheckbox,
                    mhdInfrastructureCheckbox,
                    warehouseCheckbox,
                    truckStopCheckbox,
                    intermodalTerminalCheckbox,
                    serviceCenterCheckbox,
                    truckParkingLocationCheckbox,
                    pemsTrafficCheckbox,
                    trafficMainlineCheckbox,
                    trafficOnrampCheckbox,
                    trafficOfframpCheckbox,
                    disadvantagedCommunitiesCheckbox,
                    calEnviroScreenCheckbox,
                    calEnviroScreenMinPercentile: clampCalEnviroPercentile(calEnviroScreenMinPercentile),
                    circuitCapacityCheckbox,
                    utilitiesCheckbox,
                })
            );
        } catch (_) {
            // no-op
        }
    }, [
        landParcelCheckbox,
        SCAQMDCheckbox,
        sraCheckbox,
        mhdInfrastructureCheckbox,
        warehouseCheckbox,
        truckStopCheckbox,
        intermodalTerminalCheckbox,
        serviceCenterCheckbox,
        truckParkingLocationCheckbox,
        pemsTrafficCheckbox,
        trafficMainlineCheckbox,
        trafficOnrampCheckbox,
        trafficOfframpCheckbox,
        disadvantagedCommunitiesCheckbox,
        calEnviroScreenCheckbox,
        calEnviroScreenMinPercentile,
        circuitCapacityCheckbox,
        utilitiesCheckbox,
    ]);

    const buildParcelViewportParams = useCallback((bounds, filters = {}, cursor = null, limit = PAGE_SIZE, zoomOverride = null) => {
        const params = {
            limit: limit ?? PAGE_SIZE,
            top: Math.min(TOP_PARCEL_LABEL_LIMIT, 200)
        };

        if (cursor) {
            params.cursor = cursor;
        }

        const zoomToUse = zoomOverride ?? zoomLevel ?? mapZoom ?? 10;
        if (typeof zoomToUse === 'number') {
            params.zoom = zoomToUse;
        }

        if (bounds) {
            // Send both bbox (lon/lat order) and bounds (south,west,north,east) for backend compatibility
            params.bbox = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`;
        }

        if (searchId && cursor) {
            params.search_id = searchId;
        }

        const filterKeyMap = {
            minLandValue: 'value_min',
            maxLandValue: 'value_max',
            minArea: 'area_min',
            maxArea: 'area_max',
            zipCode: 'zip',
            city: 'city',
            utility: 'utility_region',
            min_score: 'min_score',
            inScaqmdJurisdiction: 'near_scaqmd',
            inDisadvantagedCommunity: 'near_dc'
        };

        Object.entries(filterKeyMap).forEach(([localKey, apiKey]) => {
            const value = filters?.[localKey];
            if (value !== undefined && value !== null && value !== '') {
                params[apiKey] = value;
            }
        });

        return params;
    }, [PAGE_SIZE, zoomLevel, mapZoom, searchId]);

    const parcelsAbortRef = useRef(null);

    const fetchParcelsForViewport = useCallback(async ({
        bounds,
        filters = searchFilters,
        cursor = null,
        append = false,
        zoom = null,
        limit = PAGE_SIZE
    } = {}) => {
        if (!bounds) {
            logger.warn('fetchParcelsForViewport called without bounds');
            return;
        }

        const params = buildParcelViewportParams(bounds, filters, cursor, limit, zoom ?? zoomLevel);

        // Cancel any in-flight parcel request before starting a new one
        if (parcelsAbortRef.current) {
            parcelsAbortRef.current.abort();
        }
        const controller = new AbortController();
        parcelsAbortRef.current = controller;

        if (append) {
            updateMapViewState({ isLoadingMore: true });
        } else {
            updateMapViewState({ parcelLoading: true });
            setNextCursor(null);
            if (!cursor) {
                setSearchId(null);
            }
        }

        try {
            const response = await axios.get(API_ENDPOINTS.getParcelViewport, {
                params,
                signal: controller.signal,
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                }
            });

            const rawDetails = Array.isArray(response.data?.details)
                ? response.data.details
                : Array.isArray(response.data?.results)
                    ? response.data.results
                    : [];
            const summary = response.data?.summary ?? {};
            const results = normalizeParcelList(rawDetails);
            const nextCursorFromResponse = response.data?.next_cursor ?? response.data?.nextCursor ?? null;
            const searchIdFromResponse = response.data?.search_id ?? summary.search_id ?? response.data?.searchId ?? null;
            const totalFromResponse = summary?.total ?? summary?.counts?.total ?? response.data?.total_count ?? response.data?.count ?? response.data?.total;
            const baseList = append
                ? (Array.isArray(mapParcelData) && mapParcelData.length > 0
                    ? mapParcelData
                    : Array.isArray(parcelData)
                        ? parcelData
                        : [])
                : [];
            const updatedList = append ? [...baseList, ...results] : results;
            const updatedPage = append ? (currentPage || 1) + 1 : 1;
            const hasNext = Boolean(nextCursorFromResponse);

            updateParcelDisplay(updatedList, updatedList, {
                currentPage: updatedPage,
                hasNextPage: hasNext,
                totalFilteredCount: totalFromResponse ?? updatedList.length
            });
            setAllParcels(updatedList);
            setPrevMapBounds(bounds);
            lastBoundsRef.current = bounds;
            setNextCursor(nextCursorFromResponse);

            if (searchIdFromResponse) {
                setSearchId(searchIdFromResponse);
                const fallbackPointUrl = `${API_ENDPOINTS.vectorTiles.parcelPoints}?search_id=${encodeURIComponent(searchIdFromResponse)}`;
                const fallbackPolygonUrl = `${API_ENDPOINTS.vectorTiles.parcels}?search_id=${encodeURIComponent(searchIdFromResponse)}`;
                setParcelPointTileUrl(summary?.tile_url_points || fallbackPointUrl);
                setParcelPolygonTileUrl(summary?.tile_url_polygons || fallbackPolygonUrl);
            } else {
                setParcelPointTileUrl(summary?.tile_url_points || API_ENDPOINTS.vectorTiles.parcelPoints);
                setParcelPolygonTileUrl(summary?.tile_url_polygons || API_ENDPOINTS.vectorTiles.parcels);
                if (!append) {
                    setSearchId(null);
                }
            }
        } catch (error) {
            if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.name === 'AbortError') {
                logger.warn('Parcel fetch aborted (superseded by newer bounds/zoom).');
                return;
            }
            if (!append) {
                updateParcelDisplay([], [], {
                    currentPage: 1,
                    hasNextPage: false,
                    totalFilteredCount: 0
                });
                setAllParcels([]);
                setNextCursor(null);
                setSearchId(null);
                setParcelPointTileUrl(API_ENDPOINTS.vectorTiles.parcelPoints);
                setParcelPolygonTileUrl(API_ENDPOINTS.vectorTiles.parcels);
            }
            logger.error('Error fetching parcels for viewport:', error);
        } finally {
            if (parcelsAbortRef.current === controller) {
                parcelsAbortRef.current = null;
            }
            updateMapViewState({ parcelLoading: false, isLoadingMore: false });
        }
    }, [buildParcelViewportParams, mapParcelData, parcelData, searchFilters, updateParcelDisplay, setAllParcels, updateMapViewState, zoomLevel, normalizeParcelList, currentPage]);

    const debouncedFetchViewportParcels = useCallback((bounds, zoom, filters = searchFilters) => {
        if (!bounds) return;

        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }

        loadingTimeoutRef.current = setTimeout(() => {
            fetchParcelsForViewport({
                bounds,
                filters,
                cursor: null,
                append: false,
                zoom
            });
        }, 750); // slightly slower debounce to avoid request storms while panning
    }, [fetchParcelsForViewport, searchFilters]);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isLoadingMore || !mapBounds || !nextCursor) return;
        
        await fetchParcelsForViewport({
            bounds: mapBounds,
            filters: searchFilters,
            cursor: nextCursor,
            append: true,
            zoom: zoomLevel
        });
    }, [hasNextPage, isLoadingMore, mapBounds, nextCursor, searchFilters, fetchParcelsForViewport, zoomLevel]);

    // Data fetching
    const getSCAQMDJurisdiction = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;

        setScaqmdJurisdictionLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getSCAQMDJurisdiction, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            if (!signal.aborted) {
                // Debug the response structure
                logger.log('SCAQMD Jurisdiction API Response:', response.data);
                logger.log('Response status:', response.status);

                // Handle GeoJSON FeatureCollection or direct array response
                let jurisdictions = [];

                if (response.data) {
                    if (response.data.scaqmd_jurisdictions && response.data.scaqmd_jurisdictions.features) {
                        // GeoJSON FeatureCollection format
                        jurisdictions = Array.isArray(response.data.scaqmd_jurisdictions.features) ? response.data.scaqmd_jurisdictions.features : [];
                        logger.log('Processed SCAQMD jurisdictions from FeatureCollection:', jurisdictions.length);
                    } else if (Array.isArray(response.data.scaqmd_jurisdictions)) {
                        // Direct array format
                        jurisdictions = response.data.scaqmd_jurisdictions;
                        logger.log('Processed SCAQMD jurisdictions as direct array:', jurisdictions.length);
                    } else if (Array.isArray(response.data)) {
                        // Legacy direct array response
                        jurisdictions = response.data;
                        logger.log('Processed SCAQMD jurisdictions as legacy direct array:', jurisdictions.length);
                    } else {
                        logger.warn('No SCAQMD jurisdictions field or unexpected format in response');
                    }
                } else {
                    logger.warn('Response data is null or undefined');
                }

                setScaqmdJurisdiction(jurisdictions);
                logger.log(`SCAQMD Jurisdiction Data loaded: ${jurisdictions.length} jurisdictions`);
                
                if (jurisdictions.length > 0) {
                    logger.log('Sample SCAQMD jurisdiction data:', jurisdictions.slice(0, 2));
                }
                
                if (response.data && response.data.truncated) {
                    logger.warn('SCAQMD jurisdiction data was truncated due to large result set');
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching SCAQMD Jurisdiction:', error);
                logger.error('Error response:', error.response?.data);
                logger.error('Error status:', error.response?.status);
                if (!signal.aborted) {
                    setScaqmdJurisdiction([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setScaqmdJurisdictionLoading(false);
            }
        }
        return controller;
    }, []);

    const getMhdInfrastructure = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;

        setMhdInfrastructureLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getMdhdInfrastructure, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            if (!signal.aborted) {
                setMhdInfrastructure(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching MHD Infrastructure:', error);
                if (!signal.aborted) {
                    setMhdInfrastructure([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setMhdInfrastructureLoading(false);
            }
        }
        return controller;
    }, []);

    const getWarehouses = useCallback(async (bounds = null, zoom = 10) => {
        const controller = new AbortController();
        const signal = controller.signal;

        setWarehousesLoading(true);
        try {
            // Build query parameters for server-side filtering
            const params = new URLSearchParams();
            if (bounds) {
                params.append('bounds', `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
            }
            params.append('zoom', zoom.toString());

            const response = await axios.get(`${API_ENDPOINTS.getWarehouses}?${params.toString()}`, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            if (!signal.aborted) {
                // Debug the response structure
                logger.log('Warehouse API Response:', response.data);
                logger.log('Response status:', response.status);

                // Handle GeoJSON FeatureCollection or direct array response
                let warehouses = [];

                if (response.data) {
                    if (response.data.warehouses && response.data.warehouses.features) {
                        // GeoJSON FeatureCollection format
                        warehouses = Array.isArray(response.data.warehouses.features) ? response.data.warehouses.features : [];
                        logger.log('Processed warehouses from FeatureCollection:', warehouses.length);
                    } else if (Array.isArray(response.data.warehouses)) {
                        // Direct array format
                        warehouses = response.data.warehouses;
                        logger.log('Processed warehouses as direct array:', warehouses.length);
                    } else if (Array.isArray(response.data)) {
                        // Legacy direct array response
                        warehouses = response.data;
                        logger.log('Processed warehouses as legacy direct array:', warehouses.length);
                    } else {
                        logger.warn('No warehouses field or unexpected format in response');
                    }
                } else {
                    logger.warn('Response data is null or undefined');
                }

                setWarehouses(warehouses);
                logger.log(`Warehouse Data loaded: ${warehouses.length} warehouses at zoom ${zoom}`);
                
                if (warehouses.length > 0) {
                    logger.log('Sample warehouse data:', warehouses.slice(0, 2));
                }
                
                if (response.data && response.data.truncated) {
                    logger.warn('Warehouse data was truncated due to large result set');
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching Warehouses:', error);
                logger.error('Error response:', error.response?.data);
                logger.error('Error status:', error.response?.status);
                if (!signal.aborted) {
                    setWarehouses([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setWarehousesLoading(false);
            }
        }
        return controller;
    }, []);

    // const getHeatmapData = useCallback(async () => {
    //     const controller = new AbortController();
    //     const signal = controller.signal;

    //     logger.log("Starting heatmap data fetch...");
    //     logger.log("Map bounds:", mapBounds);
    //     logger.log("Zoom level:", zoomLevel);

    //     // Check if map bounds are available
    //     if (!mapBounds) {
    //         logger.log("Map bounds not available yet, skipping heatmap data fetch");
    //         return;
    //     }

    //     setHeatmapDataLoading(true);

    //     try {
    //         logger.log("Making request to:", API_ENDPOINTS.getHeatmapData);
    //         logger.log("Auth token:", localStorage.getItem('authToken') ? 'Present' : 'Missing');

    //         const response = await axios.get(API_ENDPOINTS.getHeatmapData, {
    //             params: {
    //                 bounds: `${mapBounds.south},${mapBounds.west},${mapBounds.north},${mapBounds.east}`,
    //                 zoom: zoomLevel,
    //             },
    //             headers: {
    //                 Authorization: `Token ${localStorage.getItem('authToken')}`
    //             },
    //             signal
    //         });

    //         // logger.log("Heatmap API Response Status:", response.status);
    //         // logger.log("Heatmap API Response Headers:", response.headers);
    //         // logger.log("Heatmap API Raw Response:", response);
    //         // logger.log("Heatmap API Response Data:", response.data);
    //         // logger.log("Heatmap API Response Data Type:", typeof response.data);
    //         // logger.log("Heatmap API Response Data Length:", Array.isArray(response.data) ? response.data.length : 'Not an array');
    //         // logger.log("Heatmap API Nested Data:", response.data.data);
    //         // logger.log("Heatmap API Nested Data Type:", typeof response.data.data);
    //         // logger.log("Heatmap API Nested Data Length:", Array.isArray(response.data.data) ? response.data.data.length : 'Not an array');

    //         if (!signal.aborted) {
    //             // Extract the actual data array from various possible response shapes
    //             let data = [];
    //             if (Array.isArray(response.data)) {
    //                 data = response.data;
    //                 logger.log("Heatmap: detected direct array response");
    //             } else if (response.data && Array.isArray(response.data.data)) {
    //                 data = response.data.data;
    //                 logger.log("Heatmap: detected nested data array response");
    //             } else if (response.data && Array.isArray(response.data.results)) {
    //                 data = response.data.results;
    //                 logger.log("Heatmap: detected results array response");
    //             } else {
    //                 logger.warn("Heatmap: unknown response shape; defaulting to empty array", response.data);
    //             }

    //             // Normalize weight using weight or point_count; default to 1
    //             const normalized = data.map(item => ({
    //                 ...item,
    //                 weight: typeof item.weight === 'number' ? item.weight : (typeof item.point_count === 'number' ? item.point_count : 1)
    //             }));

    //             // logger.log("Processed Heatmap Data:", normalized);
    //             // logger.log("Setting heatmap data with length:", normalized.length);
    //             setHeatmapData(normalized);

    //             // Log first few items if data exists
    //             if (normalized.length > 0) {
    //                 logger.log("First 3 heatmap items:", normalized.slice(0, 3));
    //             } else {
    //                 logger.log("Heatmap data is empty - no items returned from API");
    //             }
    //         }
    //     } catch (error) {
    //         logger.error('Detailed Heatmap Error:');
    //         logger.error('Error name:', error.name);
    //         logger.error('Error message:', error.message);
    //         logger.error('Error status:', error.response?.status);
    //         logger.error('Error response data:', error.response?.data);
    //         logger.error('Full error object:', error);

    //         if (error.name !== 'AbortError') {
    //             if (!signal.aborted) {
    //                 setHeatmapData([]);
    //             }
    //         }
    //     } finally {
    //         if (!signal.aborted) {
    //             setHeatmapDataLoading(false);
    //             logger.log("Heatmap data loading finished");
    //         }
    //     }
    // }, [mapBounds, zoomLevel]); // Add mapBounds and zoomLevel as dependencies

    const getDisadvantagedCommunities = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;

        logger.log('=== STARTING DISADVANTAGED COMMUNITIES FETCH ===');
        logger.log('API Endpoint:', API_ENDPOINTS.getDisadvantagedCommunitiesConsolidated);
        logger.log('Auth token present:', !!localStorage.getItem('authToken'));

        setDisadvantagedCommunitiesLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getDisadvantagedCommunitiesConsolidated, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            logger.log('=== DISADVANTAGED COMMUNITIES API RESPONSE ===');
            logger.log('Response status:', response.status);
            logger.log('Response headers:', response.headers);
            logger.log('Raw response data:', response.data);
            logger.log('Response data type:', typeof response.data);
            logger.log('Response data keys:', response.data ? Object.keys(response.data) : 'null');

            if (!signal.aborted) {
                // Handle GeoJSON FeatureCollection or direct array response
                let communities = [];

                if (response.data) {
                    logger.log('Processing response data...');
                    
                    if (response.data.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
                        // Direct GeoJSON FeatureCollection format
                        communities = response.data.features;
                        logger.log('✅ Processed disadvantaged communities from direct FeatureCollection:', communities.length);
                    } else if (response.data.disadvantaged_communities && response.data.disadvantaged_communities.features) {
                        // Nested GeoJSON FeatureCollection format
                        communities = Array.isArray(response.data.disadvantaged_communities.features) ? response.data.disadvantaged_communities.features : [];
                        logger.log('✅ Processed disadvantaged communities from nested FeatureCollection:', communities.length);
                    } else if (response.data.disadvantaged_communities && Array.isArray(response.data.disadvantaged_communities)) {
                        // Direct array format
                        communities = response.data.disadvantaged_communities;
                        logger.log('✅ Processed disadvantaged communities as direct array:', communities.length);
                    } else if (Array.isArray(response.data)) {
                        // Legacy direct array response
                        communities = response.data;
                        logger.log('✅ Processed disadvantaged communities as legacy direct array:', communities.length);
                    } else {
                        logger.warn('❌ No disadvantaged communities field or unexpected format in response');
                        logger.log('Available fields:', Object.keys(response.data));
                        logger.log('Response type:', response.data.type);
                    }
                } else {
                    logger.warn('❌ Response data is null or undefined');
                }

                setDisadvantagedCommunities(communities);
                logger.log(`🎯 Final result: ${communities.length} disadvantaged communities loaded`);
                
                if (communities.length > 0) {
                    logger.log('📋 Sample community data (first 2):');
                    logger.log('Community 1:', communities[0]);
                    if (communities[1]) logger.log('Community 2:', communities[1]);
                } else {
                    logger.warn('⚠️ No communities loaded - checking response structure');
                    logger.log('Full response for debugging:', JSON.stringify(response.data, null, 2));
                }
                
                if (response.data && response.data.truncated) {
                    logger.warn('Disadvantaged communities data was truncated due to large result set');
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
                logger.error('❌ Error fetching Disadvantaged Communities:', error);
                logger.error('Error response:', error.response?.data);
                logger.error('Error status:', error.response?.status);
                logger.error('Error config:', error.config);
                setDisadvantagedCommunities([]);
            }
        } finally {
            if (!signal.aborted) {
                setDisadvantagedCommunitiesLoading(false);
                logger.log('=== DISADVANTAGED COMMUNITIES FETCH COMPLETED ===');
            }
        }
        return controller;
    }, []);

    const getSRAData = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;
        setSraDataLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getSCAQMDSourceReceptorAreas, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });
            if (!signal.aborted) {
                const data = response.data;
                const list = Array.isArray(data) ? data : (data?.features ?? []);
                setSraData(list);
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
                logger.error('Error fetching SRA data:', error);
                if (!signal.aborted) {
                    setSraData([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setSraDataLoading(false);
            }
        }
        return controller;
    }, []);

    const getCalEnviroScreen = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;

        const percentile = Number(calEnviroScreenMinPercentile);
        const normalizedPercentile = Number.isFinite(percentile)
            ? Math.max(0, Math.min(100, percentile))
            : 75;

        setCalEnviroScreenLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getCalEnviroScreen40, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                params: {
                    min_ciscorep: normalizedPercentile
                },
                signal
            });

            if (!signal.aborted) {
                let features = [];
                if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
                    features = response.data.features;
                } else if (Array.isArray(response.data)) {
                    features = response.data;
                }
                setCalEnviroScreen(features);
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
                logger.error('Error fetching CalEnviroScreen 4.0:', error);
                setCalEnviroScreen([]);
            }
        } finally {
            if (!signal.aborted) {
                setCalEnviroScreenLoading(false);
            }
        }
        return controller;
    }, [calEnviroScreenMinPercentile]);

    const getTruckStops = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;

        setTruckStopsLoading(true);
        try {
            const intermodalTerminalsResponse = await axios.get(API_ENDPOINTS.getIntermodalTerminals, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            const intermodalTerminals = intermodalTerminalsResponse.data.map((terminal) => {
                return {
                    ...terminal,
                    type: 'intermodal_terminal'
                }
            });

            const serviceCentersResponse = await axios.get(API_ENDPOINTS.getServiceCenters, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            const serviceCenters = serviceCentersResponse.data.map((center) => {
                return {
                    ...center,
                    type: 'service_center'
                }
            });

            if (!signal.aborted) {
                setTruckStops(intermodalTerminals.concat(serviceCenters));
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching Truck Stops:', error);
                if (!signal.aborted) {
                    setTruckStops([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setTruckStopsLoading(false);
            }
        }
        return controller;
    }, []);

    const getCircuitData = useCallback(async (bounds = null, zoom = 10) => {
        const controller = new AbortController();
        const signal = controller.signal;

        setCircuitDataLoading(true);
        setCurrentZoom(zoom);

        try {
            // Determine which data to fetch based on zoom level
            const ZOOM_THRESHOLD = 13;
            
            if (zoom > ZOOM_THRESHOLD) {
                // High zoom level: Fetch detailed circuit lines
                logger.log(`Fetching detailed circuit lines at zoom ${zoom}`);
                
                // Build query parameters for server-side filtering
                const params = new URLSearchParams();
                if (bounds) {
                    params.append('bounds', `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
                }
                params.append('zoom', zoom.toString());

                const response = await axios.get(`${API_ENDPOINTS.getCircuits}?${params.toString()}`, {
                    headers: {
                        Authorization: `Token ${localStorage.getItem('authToken')}`
                    },
                    signal
                });

                if (!signal.aborted) {
                    // Debug the response structure
                    logger.log('Circuit API Response:', response.data);

                    // Handle GeoJSON FeatureCollection response structure
                    let circuits = [];
                    let circuits_ladwp = [];

                    if (response.data) {
                        // Handle GeoJSON FeatureCollection format
                        if (response.data.circuits && response.data.circuits.features) {
                            circuits = Array.isArray(response.data.circuits.features) ? response.data.circuits.features : [];
                            logger.log('Processed circuits from FeatureCollection:', circuits.length);
                        } else if (Array.isArray(response.data.circuits)) {
                            circuits = response.data.circuits;
                            logger.log('Processed circuits as direct array:', circuits.length);
                        }

                        if (response.data.circuits_ladwp && response.data.circuits_ladwp.features) {
                            circuits_ladwp = Array.isArray(response.data.circuits_ladwp.features) ? response.data.circuits_ladwp.features : [];
                            logger.log('Processed circuits_ladwp from FeatureCollection:', circuits_ladwp.length);
                        } else if (Array.isArray(response.data.circuits_ladwp)) {
                            circuits_ladwp = response.data.circuits_ladwp;
                            logger.log('Processed circuits_ladwp as direct array:', circuits_ladwp.length);
                        }

                        // Check if the response might be a direct array (different API structure)
                        if (Array.isArray(response.data) && response.data.length > 0) {
                            logger.log('Response is a direct array, treating as circuits');
                            circuits = response.data;
                        }
                    }
                    
                    const combinedCircuits = [
                        ...circuits.map(circuit => ({ ...circuit, type: 'regular' })),
                        ...circuits_ladwp.map(circuit => ({ ...circuit, type: 'ladwp' }))
                    ];
                    
                    setCircuitData(combinedCircuits);
                    setCircuitContours([]); // Clear contours when showing circuit lines
                    logger.log(`Circuit lines loaded: ${combinedCircuits.length} circuits at zoom ${zoom}`);
                    
                    if (combinedCircuits.length > 0) {
                        logger.log('Sample circuit data:', combinedCircuits.slice(0, 2));
                    }
                }
            } else {
                // Low zoom level: Fetch capacity contour multipolygons
                logger.log(`Fetching circuit capacity contours at zoom ${zoom}`);
                
                const params = new URLSearchParams();
                if (bounds) {
                    params.append('bounds', `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
                }
                params.append('zoom', zoom.toString());

                const response = await axios.get(`${API_ENDPOINTS.getCircuitContours}?${params.toString()}`, {
                    headers: {
                        Authorization: `Token ${localStorage.getItem('authToken')}`
                    },
                    signal
                });

                if (!signal.aborted) {
                    logger.log('Circuit Contour API Response:', response.data);

                    // Handle GeoJSON FeatureCollection for contours
                    let contours = [];

                    if (response.data) {
                        if (response.data.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
                            contours = response.data.features;
                            logger.log('Processed contours from FeatureCollection:', contours.length);
                        } else if (response.data.features && Array.isArray(response.data.features)) {
                            contours = response.data.features;
                            logger.log('Processed contours from features array:', contours.length);
                        } else if (Array.isArray(response.data)) {
                            contours = response.data;
                            logger.log('Processed contours as direct array:', contours.length);
                        }
                    }

                    setCircuitContours(contours);
                    setCircuitData([]); // Clear circuit lines when showing contours
                    logger.log(`Circuit contours loaded: ${contours.length} contours at zoom ${zoom}`);

                    if (contours.length > 0) {
                        logger.log('Sample contour data:', contours.slice(0, 2));
                    }
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
                logger.error('Error fetching Circuit Data:', error);
                logger.error('Error response:', error.response?.data);
                logger.error('Error status:', error.response?.status);
                setCircuitData([]);
                setCircuitContours([]);
            }
        } finally {
            if (!signal.aborted) {
                setCircuitDataLoading(false);
            }
        }
        return controller;
    }, []);

    const getTrafficData = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;
        
        setTrafficDataLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getTrafficData, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });

            if (!signal.aborted) {
                const normalized = Array.isArray(response.data)
                    ? response.data.map(point => ({
                        ...point,
                        station_type: (point.station_type || 'ML').toString().trim().toUpperCase(),
                        truck_volume: point.truck_volume ?? point.volume ?? null,
                        vehicle_volume: point.vehicle_volume ?? null,
                    }))
                    : [];
                setTrafficData(normalized);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching Traffic Data:', error);
                if (!signal.aborted) {
                    setTrafficData([]);
                }
            }
        }   
        finally {
            if (!signal.aborted) {
                setTrafficDataLoading(false);
            }
        }
        return controller;
    }, []);

    const getUtilitiesData = useCallback(async () => {
        const controller = new AbortController();
        const signal = controller.signal;
        
        setUtilitiesDataLoading(true);
        try {
            const response = await axios.get(API_ENDPOINTS.getUtilityLoadmap, {
                headers: {
                    Authorization: `Token ${localStorage.getItem('authToken')}`
                },
                signal
            });
            
            if (!signal.aborted) {
                logger.log("Utilities Data:", response.data);
                setUtilitiesData(response.data);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                logger.error('Error fetching Utilities Data:', error);
                if (!signal.aborted) {
                    setUtilitiesData([]);
                }
            }
        } finally {
            if (!signal.aborted) {
                setUtilitiesDataLoading(false);
            }
        }
        return controller;
    }, []); 
    
    // Lazy-load data layers only when their checkboxes are enabled
    useEffect(() => {
        if (!warehouseCheckbox) return;
        getWarehouses(mapBounds, zoomLevel);
    }, [warehouseCheckbox, mapBounds, zoomLevel, getWarehouses]);

    useEffect(() => {
        if (!mhdInfrastructureCheckbox) return;
        getMhdInfrastructure();
    }, [mhdInfrastructureCheckbox, getMhdInfrastructure]);

    useEffect(() => {
        if (!SCAQMDCheckbox) return;
        getSCAQMDJurisdiction();
    }, [SCAQMDCheckbox, getSCAQMDJurisdiction]);

    useEffect(() => {
        if (!sraCheckbox) return;
        getSRAData();
    }, [sraCheckbox, getSRAData]);

    useEffect(() => {
        if (!disadvantagedCommunitiesCheckbox) return;
        getDisadvantagedCommunities();
    }, [disadvantagedCommunitiesCheckbox, getDisadvantagedCommunities]);

    useEffect(() => {
        if (!calEnviroScreenCheckbox) return;
        getCalEnviroScreen();
    }, [calEnviroScreenCheckbox, getCalEnviroScreen, calEnviroScreenMinPercentile]);

    useEffect(() => {
        if (!(truckStopCheckbox || intermodalTerminalCheckbox || serviceCenterCheckbox || truckParkingLocationCheckbox)) return;
        getTruckStops();
    }, [truckStopCheckbox, intermodalTerminalCheckbox, serviceCenterCheckbox, truckParkingLocationCheckbox, getTruckStops]);

    useEffect(() => {
        if (!circuitCapacityCheckbox) return;
        getCircuitData(mapBounds, zoomLevel);
    }, [circuitCapacityCheckbox, mapBounds, zoomLevel, getCircuitData]);

    useEffect(() => {
        if (!pemsTrafficCheckbox) return;
        getTrafficData();
    }, [pemsTrafficCheckbox, getTrafficData]);

    useEffect(() => {
        if (!utilitiesCheckbox) return;
        getUtilitiesData();
    }, [utilitiesCheckbox, getUtilitiesData]);

    // useEffect(() => {
    //     if (!truckParkingLocationCheckbox) return;
    //     getHeatmapData();
    // }, [truckParkingLocationCheckbox, getHeatmapData]);
    
    // Add helper to decide if we should fetch based on bounds delta
    const shouldFetchForBoundsChange = (prev, next) => {
        if (!prev) return true; // First time
        
        // Calculate the intersection of current view bbox with the last fetched bbox
        const interLat = Math.max(0, Math.min(prev.north, next.north) - Math.max(prev.south, next.south));
        const interLng = Math.max(0, Math.min(prev.east, next.east) - Math.max(prev.west, next.west));
        const interArea = interLat * interLng;
        
        // Calculate the area of the current view bbox
        const currentArea = Math.max(0, (next.north - next.south)) * Math.max(0, (next.east - next.west));
        
        // Calculate how much of the current view is covered by the previous fetch
        const coverage = currentArea > 0 ? interArea / currentArea : 0;
        
        // Only fetch when less than 20% of current view is covered by previous fetch
        const shouldFetch = coverage < 0.2;
        
        logger.log(`Bounds overlap analysis:`, {
            prevBounds: `${prev.south.toFixed(4)},${prev.west.toFixed(4)},${prev.north.toFixed(4)},${prev.east.toFixed(4)}`,
            currentBounds: `${next.south.toFixed(4)},${next.west.toFixed(4)},${next.north.toFixed(4)},${next.east.toFixed(4)}`,
            intersectionArea: interArea.toFixed(6),
            currentViewArea: currentArea.toFixed(6),
            coveragePercent: `${(coverage * 100).toFixed(1)}%`,
            shouldFetch: shouldFetch,
            reason: shouldFetch ? 'Coverage < 20%' : 'Sufficient overlap'
        });
        
        return shouldFetch;
    };

    // Fetch initial parcel data when map bounds become available
    useEffect(() => {
        if (mapBounds && !lastBoundsRef.current) {
            logger.log("Initial map bounds available, fetching parcel data");
            fetchParcelsForViewport({
                bounds: mapBounds,
                filters: searchFilters,
                append: false,
                zoom: zoomLevel
            });
        }
    }, [mapBounds, fetchParcelsForViewport, searchFilters, zoomLevel]); // Only trigger first time when bounds appear

    // // Auto-fetch heatmap on bounds or zoom changes with debounce
    // useEffect(() => {
    //     if (!mapBounds) return;

    //     // Debounce heatmap fetch to avoid excessive requests during interactions
    //     if (heatmapTimeoutRef.current) {
    //         clearTimeout(heatmapTimeoutRef.current);
    //     }
    //     heatmapTimeoutRef.current = setTimeout(() => {
    //         logger.log("Auto-fetching heatmap due to bounds/zoom change");
    //         getHeatmapData();
    //     }, 400);

    //     return () => {
    //         if (heatmapTimeoutRef.current) {
    //             clearTimeout(heatmapTimeoutRef.current);
    //             heatmapTimeoutRef.current = null;
    //         }
    //     };
    // }, [mapBounds, zoomLevel, getHeatmapData]);

    // Additional cleanup effect for component unmount
    useEffect(() => {
        return () => {
            // Clean up any remaining timeouts
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            if (heatmapTimeoutRef.current) {
                clearTimeout(heatmapTimeoutRef.current);
                heatmapTimeoutRef.current = null;
            }

        };
    }, []); // Empty dependency array - only runs on unmount

    // Handle map bounds change - automatically fetch data
    const handleMapBoundsChange = useCallback((bounds, zoom) => {
        if (!bounds || typeof zoom !== 'number') return;

        const center = {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2
        };

        updateMapPosition(center, zoom, bounds);
        setZoomLevel(zoom);

        logger.log(`prevMapBounds: ${prevMapBounds?.south},${prevMapBounds?.west},${prevMapBounds?.north},${prevMapBounds?.east}, bounds: ${bounds.south},${bounds.west},${bounds.north},${bounds.east}`);
        const shouldFetch = shouldFetchForBoundsChange(prevMapBounds, bounds);
        logger.log("Bounds changed. Should fetch:", shouldFetch, { prev: prevMapBounds, next: bounds });

        if (shouldFetch) {
            debouncedFetchViewportParcels(bounds, zoom, searchFilters);
        }
    }, [searchFilters, debouncedFetchViewportParcels, updateMapPosition, prevMapBounds]);

    // Restore map position and state when returning to map view
    useEffect(() => {
        if (!isLoaded) return;
        if (!mapRef.current || !mapCenter || mapZoom === null || mapZoom === undefined) return;
        if (hasRestoredInitialViewRef.current) return;

        logger.log('Restoring saved map position:', { center: mapCenter, zoom: mapZoom });
        if (mapRef.current.restorePosition) {
            mapRef.current.restorePosition(mapCenter, mapZoom);
        }
        hasRestoredInitialViewRef.current = true;
    }, [mapCenter, mapZoom, isLoaded]);

    const waitForMapIdle = useCallback((mapInstance, timeout = 800) => {
        if (!mapInstance || !window.google?.maps?.event) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let resolved = false;
            const listener = window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
                if (!resolved) {
                    resolved = true;
                    resolve();
                }
            });

            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    if (listener) {
                        window.google.maps.event.removeListener(listener);
                    }
                    resolve();
                }
            }, timeout);
        });
    }, []);

    // Handle search by address - geocode the address and fetch parcels near the result
    const handleSearchByAddress = useCallback(async (newFilters = {}, weightedParcels = null) => {
        updateMapViewState({ isFiltering: true });
        updateSearchState(newFilters, true);

        try {
            if (Array.isArray(weightedParcels) && weightedParcels.length > 0) {
                logger.log(`Displaying ${weightedParcels.length} weighted parcels`);
                const normalizedWeighted = normalizeParcelList(weightedParcels);
                updateParcelDisplay(normalizedWeighted, normalizedWeighted, {
                    currentPage: 1,
                    hasNextPage: false,
                    totalFilteredCount: normalizedWeighted.length
                });
                setAllParcels(normalizedWeighted);
                setSearchId(null);
                setNextCursor(null);
                setParcelPointTileUrl(API_ENDPOINTS.vectorTiles.parcelPoints);
                setParcelPolygonTileUrl(API_ENDPOINTS.vectorTiles.parcels);
                return;
            }

            if (!newFilters?.address) {
                if (mapBounds) {
                    await fetchParcelsForViewport({
                        bounds: mapBounds,
                        filters: newFilters,
                        append: false,
                        zoom: zoomLevel
                    });
                }
                return;
            }

            const geocoder = await initializeGeocoder();
            const geocodeResult = await geocodeAddress(geocoder, newFilters.address, 'us');

            if (!geocodeResult) {
                logger.warn('Geocoding failed for address search, falling back to current viewport.');
                if (mapBounds) {
                    await fetchParcelsForViewport({
                        bounds: mapBounds,
                        filters: newFilters,
                        append: false,
                        zoom: zoomLevel
                    });
                }
                return;
            }

            const { lat, lng } = geocodeResult;
            const googleMap = mapRef.current?.getMap?.();

            if (googleMap) {
                googleMap.panTo({ lat, lng });
                if (googleMap.getZoom && googleMap.getZoom() < 16) {
                    googleMap.setZoom(16);
                }
                await waitForMapIdle(googleMap);
            }

            const boundsFromMap = mapRef.current?.getBounds?.();
            const fallbackBounds = boundsFromMap || {
                north: lat + 0.02,
                south: lat - 0.02,
                east: lng + 0.02,
                west: lng - 0.02
            };

            updateMapPosition({ lat, lng }, googleMap?.getZoom?.() ?? 16, fallbackBounds);

            await fetchParcelsForViewport({
                bounds: fallbackBounds,
                filters: newFilters,
                append: false,
                zoom: googleMap?.getZoom?.() ?? 16
            });
        } catch (error) {
            logger.error('Error performing address search:', error);
            if (mapBounds) {
                await fetchParcelsForViewport({
                    bounds: mapBounds,
                    filters: newFilters,
                    append: false,
                    zoom: zoomLevel
                });
            }
        } finally {
            updateMapViewState({ isFiltering: false });
        }
    }, [fetchParcelsForViewport, mapBounds, mapRef, updateMapPosition, updateMapViewState, updateParcelDisplay, setAllParcels, updateSearchState, waitForMapIdle, zoomLevel, normalizeParcelList]);

    // Handle search in current viewport - server-side filtering based on current bounds
    const handleSearchInViewport = useCallback(async (newFilters = {}) => {
        updateMapViewState({ isFiltering: true });
        updateSearchState(newFilters, true);

        const effectiveBounds = mapBounds || mapRef.current?.getBounds?.();
        if (!effectiveBounds) {
            updateMapViewState({ isFiltering: false });
            return;
        }

        await fetchParcelsForViewport({
            bounds: effectiveBounds,
            filters: newFilters,
            append: false,
            zoom: zoomLevel
        });

        updateMapViewState({ isFiltering: false });
    }, [mapBounds, fetchParcelsForViewport, updateMapViewState, updateSearchState, zoomLevel, mapRef]);

    // Helper function to get parcel key (same as in ParcelContext)
    const getParcelKey = useCallback((parcel) => {
        if (!parcel) return undefined;
        return parcel.id ?? `${parcel.APN ?? ""}-${parcel.Zip5 ?? parcel.Zip ?? ""}-${parcel.Address ?? ""}`;
    }, []);

    // Handle parcel click from map
    const handleParcelClick = useCallback((parcel) => {
        const parcelId = getParcelKey(parcel);
        logger.log('Parcel clicked on map:', { parcelId, parcel });
        updateHighlightedParcel(parcelId);
        
        // Check if the clicked parcel is in the current parcel list
        const currentParcels = mapParcelData.length > 0 ? mapParcelData : parcelData;
        const isParcelInList = currentParcels.some(p => getParcelKey(p) === parcelId);
        
        if (!isParcelInList) {
            logger.log('Clicked parcel not in current list, adding it temporarily');
            // Add the clicked parcel to the current parcel data so it's visible in the list
            if (mapParcelData.length > 0) {
                updateParcelDisplay([...mapParcelData, parcel], mapParcelData, {
                    currentPage,
                    hasNextPage,
                    totalFilteredCount: totalFilteredCount + 1
                });
            } else {
                updateParcelDisplay([...parcelData, parcel], [], {
                    currentPage,
                    hasNextPage,
                    totalFilteredCount: totalFilteredCount + 1
                });
            }
        }
    }, [updateHighlightedParcel, getParcelKey, mapParcelData, parcelData, updateParcelDisplay, currentPage, hasNextPage, totalFilteredCount]);

    // Handle map bounds and position updates
    // const handleMapUpdate = useCallback((newBounds, newCenter, newZoom) => {
    //     updateMapPosition(newCenter, newZoom, newBounds);
    //     setZoomLevel(newZoom);
    // }, [updateMapPosition]);

    // Handle search in area - called from map button to search current viewport
    const handleSearchInArea = useCallback(async () => {
        if (!mapBounds) return;

        logger.log("Manual search triggered - searching current viewport area");
        // Call handleSearchInViewport with current filters
        await handleSearchInViewport(searchFilters);
    }, [mapBounds, handleSearchInViewport, searchFilters]);

    const dataLayerGroups = useMemo(() => [
        {
            title: 'Charging Infrastructure',
            items: [
                {
                    id: 'mhdInfrastructure',
                    label: 'Charging Stations',
                    description: 'MDHD charging stations that are open, under development, or planned (Source: CalSTART).',
                    checked: mhdInfrastructureCheckbox,
                    onChange: setMhdInfrastructureCheckbox,
                    icon: FiZap,
                    iconClass: 'text-amber-500'
                },
            ]
        },
        {
            title: 'Trucking Infrastructure',
            items: [
                {
                    id: 'warehouses',
                    label: 'Warehouses',
                    description: 'Warehouse footprint across Southern California (Warehouse CITY).',
                    checked: warehouseCheckbox,
                    onChange: setWarehouseCheckbox,
                    icon: FiPackage,
                    iconClass: 'text-teal-600'
                },
                {
                    id: 'intermodalTerminals',
                    label: 'Intermodal Terminals',
                    description: 'Ocean ports, rail terminals, and equipment depots (LoadMatch).',
                    checked: intermodalTerminalCheckbox,
                    onChange: setIntermodalTerminalCheckbox,
                    icon: FiShare2,
                    iconClass: 'text-indigo-500'
                },
                {
                    id: 'serviceCenters',
                    label: 'Service Centers',
                    description: 'Service centers providing fueling, maintenance, and rest options for trucks.',
                    checked: serviceCenterCheckbox,
                    onChange: setServiceCenterCheckbox,
                    icon: FiTool,
                    iconClass: 'text-gray-600'
                },
            ]
        },
        {
            title: 'Truck Activity',
            items: [
                {
                    id: 'truckHotspots',
                    label: 'Truck Hotspots',
                    description: 'Aggregated locations where trucks park for extended periods.',
                    checked: truckParkingLocationCheckbox,
                    onChange: setTruckParkingLocationCheckbox,
                    icon: FiMapPin,
                    iconClass: 'text-rose-500'
                },
                {
                    id: 'traffic',
                    label: 'Traffic Volume',
                    description: 'Truck traffic volumes along major California corridors (PeMS).',
                    checked: pemsTrafficCheckbox,
                    onChange: setPemsTrafficCheckbox,
                    icon: PiTrafficSignal,
                    iconClass: 'text-emerald-600'
                },
                // {
                //     id: 'trafficMainline',
                //     label: 'Mainline Stations',
                //     description: 'Display mainline stations (ML).',
                //     checked: trafficMainlineCheckbox,
                //     onChange: setTrafficMainlineCheckbox,
                //     icon: PiTrafficSignal,
                //     iconClass: 'text-green-600'
                // },
                // {
                //     id: 'trafficOnramp',
                //     label: 'On-Ramp Stations',
                //     description: 'Display on-ramp metering stations (FR).',
                //     checked: trafficOnrampCheckbox,
                //     onChange: setTrafficOnrampCheckbox,
                //     icon: FiCornerUpRight,
                //     iconClass: 'text-amber-500'
                // },
                // {
                //     id: 'trafficOfframp',
                //     label: 'Off-Ramp Stations',
                //     description: 'Display off-ramp stations (OR).',
                //     checked: trafficOfframpCheckbox,
                //     onChange: setTrafficOfframpCheckbox,
                //     icon: FiCornerDownRight,
                //     iconClass: 'text-rose-500'
                // }
            ]
        },
        {
            title: 'Power Grid Information',
            items: [
                {
                    id: 'gridCapacity',
                    label: 'Power Grid Capacity',
                    description: 'Circuit hosting capacity data from SCE and LADWP.',
                    checked: circuitCapacityCheckbox,
                    onChange: setCircuitCapacityCheckbox,
                    icon: FiTrendingUp,
                    iconClass: 'text-purple-600'
                },
                {
                    id: 'utilities',
                    label: 'Utilities',
                    description: 'Utility service territories.',
                    checked: utilitiesCheckbox,
                    onChange: setUtilitiesCheckbox,
                    icon: FiGrid,
                    iconClass: 'text-blue-600'
                }
            ]
        },
        {
            title: 'Regulatory Boundaries',
            items: [
                {
                    id: 'scaqmd',
                    label: 'SCAQMD Jurisdiction',
                    description: 'Projects inside SCAQMD may qualify for incentive programs.',
                    checked: SCAQMDCheckbox,
                    onChange: setSCAQMDCheckbox,
                    icon: FiFlag,
                    iconClass: 'text-orange-500'
                },
                {
                    id: 'overburdened',
                    label: 'EPA CEJST Disadvantaged Communities',
                    description: 'Communities defined by the Inflation Reduction Act as disadvantaged.',
                    checked: disadvantagedCommunitiesCheckbox,
                    onChange: setDisadvantagedCommunitiesCheckbox,
                    icon: FiAlertTriangle,
                    iconClass: 'text-red-500'
                },
                {
                    id: 'sra',
                    label: 'SCAQMD Source Receptor Areas',
                    description: 'Source receptor areas with MONITORING classification.',
                    checked: sraCheckbox,
                    onChange: setSraCheckbox,
                    icon: FiMapPin,
                    iconClass: 'text-cyan-600'
                },
                {
                    id: 'calenviroscreen40',
                    label: 'CalEnviroScreen 4.0',
                    description: 'California census tracts ranked by cumulative pollution and socioeconomic burden (OEHHA).',
                    checked: calEnviroScreenCheckbox,
                    onChange: setCalEnviroScreenCheckbox,
                    numericFilter: {
                        label: 'Percentile',
                        value: calEnviroScreenMinPercentile,
                        min: 0,
                        max: 100,
                        step: 1,
                        onChange: (nextValue) => {
                            const parsed = Number(nextValue);
                            if (!Number.isFinite(parsed)) {
                                setCalEnviroScreenMinPercentile(75);
                                return;
                            }
                            setCalEnviroScreenMinPercentile(Math.max(0, Math.min(100, parsed)));
                        }
                    },
                    icon: FiAlertTriangle,
                    iconClass: 'text-amber-600'
                }
            ]
        },
    ], [
        mhdInfrastructureCheckbox,
        warehouseCheckbox,
        intermodalTerminalCheckbox,
        serviceCenterCheckbox,
        truckParkingLocationCheckbox,
        pemsTrafficCheckbox,
        SCAQMDCheckbox,
        sraCheckbox,
        disadvantagedCommunitiesCheckbox,
        calEnviroScreenCheckbox,
        calEnviroScreenMinPercentile,
        circuitCapacityCheckbox,
        utilitiesCheckbox
    ]);

    const activeDataLayerCount = useMemo(() => {
        return dataLayerGroups.reduce((sum, group) => (
            sum + group.items.filter(item => item.checked).length
        ), 0);
    }, [dataLayerGroups]);

    if (!isLoaded) {
        return <div className="flex items-center justify-center h-[90vh] text-gray-600">Loading Google Maps...</div>;
    }
    if (loadError) {
        return <div className="flex items-center justify-center h-[90vh] text-red-600">Error loading Google Maps: {loadError.message}</div>;
    }

    return (
            <main className="h-[100dvh] bg-gray-50 font-ui">
                <div className="pt-4 px-4 pb-4 h-full">
                    <div className="flex gap-4 h-full">
                        <div className="flex-1 flex flex-col h-full">
                            <ChargerGoogleMap
                                ref={mapRef}
                                landParcelData={parcelSelection}
                                topParcelPoints={topParcelPoints}
                                onBoundsChange={handleMapBoundsChange}
                                onParcelClick={handleParcelClick}
                                scaqmdJurisdiction={scaqmdJurisdiction}
                                sraData={sraData}
                                mhdInfrastructure={mhdInfrastructure}
                                warehouses={warehouses}
                                handleSearchInArea={handleSearchInArea}
                                heatmapData={heatmapData}
                                disadvantagedCommunities={disadvantagedCommunities}
                                calEnviroScreen={calEnviroScreen}
                                truckStops={truckStops}
                                circuitData={circuitData}
                                circuitContours={circuitContours}
                                currentZoom={currentZoom}
                                trafficData={filteredTrafficData}
                                trafficStationFilters={trafficStationFilters}
                                utilitiesData={utilitiesData}
                                loading={parcelLoading}
                                highlightedParcelId={highlightedParcelId}
                                updateHighlightedParcel={updateHighlightedParcel}
                                parcelFilters={parcelTileFilters}
                                searchId={searchId}
                                parcelPointTileUrl={parcelPointTileUrl}
                                parcelPolygonTileUrl={parcelPolygonTileUrl}
                                landParcelCheckbox={landParcelCheckbox}
                                SCAQMDCheckbox={SCAQMDCheckbox}
                                sraCheckbox={sraCheckbox}
                                mhdInfrastructureCheckbox={mhdInfrastructureCheckbox}
                                warehouseCheckbox={warehouseCheckbox}
                                truckStopCheckbox={truckStopCheckbox}
                                intermodalTerminalCheckbox={intermodalTerminalCheckbox}
                                serviceCenterCheckbox={serviceCenterCheckbox}
                                truckParkingLocationCheckbox={truckParkingLocationCheckbox}
                                pemsTrafficCheckbox={pemsTrafficCheckbox}
                                disadvantagedCommunitiesCheckbox={disadvantagedCommunitiesCheckbox}
                                calEnviroScreenCheckbox={calEnviroScreenCheckbox}
                                circuitCapacityCheckbox={circuitCapacityCheckbox}
                                utilitiesCheckbox={utilitiesCheckbox}
                            />
                            {/* <div className="text-gray-500 text-sm mt-4">
                                Our platform is constantly improving, and we are working hard to add more features! 
                            </div> */}
                        </div>
                        <div className="flex-1 h-full">
                            <LandParcelSuggestionPanel
                                parcels={parcelSelection}
                                loading={parcelLoading}
                                totalCount={totalFilteredCount}
                                mapParcelCount={mapParcelData.length}
                                currentPage={currentPage}
                                pageSize={PANEL_PAGE_SIZE}
                                onSearch={handleSearchByAddress}
                                searchFilters={searchFilters}
                                isFiltering={isFiltering}
                                hasMoreData={hasNextPage}
                                onLoadMore={handleLoadMore}
                                isLoadingMore={isLoadingMore}
                                highlightedParcelId={highlightedParcelId}
                                mapRef={mapRef}
                                dataLayerGroups={dataLayerGroups}
                                dataLayerCount={activeDataLayerCount}
                            />
                        </div>
                    </div>
                </div>

                {/* Loading indicators */}
                <div className="absolute bottom-4 right-4 space-y-2">
                    {getLoadingIndicators().map((indicator, index) => (
                        <LoadingIndicator
                            key={index}
                            isVisible={indicator.isVisible}
                            text={indicator.text}
                            color={indicator.color}
                        />
                    ))}
                    {/* <button onClick={() => getHeatmapData()} className="bg-white p-4" >Test fetch heatmap</button> */}
                </div>
            </main>
    );
}

export default ChargerMapView;